import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, Plus, X, ArrowDown, ArrowUp, Receipt, Camera, Flag, Trash, PencilSimple,
  DownloadSimple, Warning,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";
import CameraCapture from "../components/CameraCapture";
import { istToday, istMonth } from "../lib/day";

const CATEGORIES = [
  "Repairs & Maintenance", "Plumbing", "Electrical", "Housekeeping", "Security",
  "Gardening", "Pest Control", "Stationery & Printing", "Refreshments",
  "Transport / Conveyance", "Staff Advance", "Festival / Events",
  "Postage / Courier", "Water / Tanker", "Fuel / Generator", "Miscellaneous",
];
const MODES = ["CASH", "UPI", "CARD", "BANK"];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const monthOpts = () => {
  const out = [];
  const [y, m] = istMonth().split("-").map(Number); // current IST year + month (1-12)
  // Build via Date.UTC at midnight UTC so the month never rolls back across the IST/UTC gap.
  for (let i = 0; i < 12; i++) { out.push(new Date(Date.UTC(y, m - 1 - i, 1)).toISOString().slice(0, 7)); }
  return out;
};
const monthLabel = (m) => new Date(m + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return d; } };

export default function CashBook() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const canFlag = ["CHAIRPERSON", "ADMIN"].includes(user?.role);
  const [month, setMonth] = useState(monthOpts()[0]);
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/cashbook?month=${month}`).then(({ data }) => setData(data)).catch(() => {});
  }, [month]);
  useEffect(() => { setData(null); load(); }, [load]);

  const s = data?.summary || { balance: 0, monthIn: 0, monthOut: 0, byCategory: {} };
  const items = data?.items || [];

  async function exportCsv() {
    try {
      const res = await api.get(`/api/cashbook/export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `cashbook-${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  }
  async function del(t) {
    if (!(await confirm({ title: "Void this entry?", message: `${t.category} · ${inr(t.amount)}. This cannot be undone.`, confirmText: "Void", danger: true }))) return;
    await api.delete(`/api/cashbook/${t.id}`); load();
  }
  async function flag(t) {
    await api.post(`/api/cashbook/${t.id}/flag`, {}); load();
  }

  return (
    <div className="space-y-5 font-sans" data-testid="cashbook-screen">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Petty Cash</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Cash Book</h1>
          <p className="mt-1 text-sm text-white/55">Day-to-day society cash, on the record.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="field !w-auto !py-2.5 text-[13px]" data-testid="cash-month">
            {monthOpts().map((m) => <option key={m} value={m} className="bg-ink-800">{monthLabel(m)}</option>)}
          </select>
          <button onClick={() => setAddOpen(true)} data-testid="cash-add-btn"
            className="inline-flex items-center gap-2 rounded-2xl bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan active:scale-95">
            <Plus size={16} weight="bold" /> Entry
          </button>
        </div>
      </div>

      {/* Balance + month flows */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="card relative overflow-hidden p-5">
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-neon-cyan/20 blur-3xl" />
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan"><Wallet size={13} weight="fill" /> Cash in hand</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-white">{inr(s.balance)}</p>
        </div>
        <div className="card p-5">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-lime"><ArrowDown size={13} weight="bold" /> Added ({monthLabel(month).split(" ")[0]})</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-white">{inr(s.monthIn)}</p>
        </div>
        <div className="card p-5">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-pink"><ArrowUp size={13} weight="bold" /> Spent ({monthLabel(month).split(" ")[0]})</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-white">{inr(s.monthOut)}</p>
        </div>
      </div>

      {/* By category */}
      {Object.keys(s.byCategory || {}).length > 0 && (
        <div className="card p-4">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Spend by category</p>
          <div className="space-y-1.5">
            {Object.entries(s.byCategory).map(([cat, amt]) => {
              const pct = s.monthOut > 0 ? Math.round((amt / s.monthOut) * 100) : 0;
              return (
                <div key={cat}>
                  <div className="flex justify-between text-[12px]"><span className="text-white/75">{cat}</span><span className="font-mono text-white/90">{inr(amt)}</span></div>
                  <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-neon-cyan" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Transactions</p>
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-neon-cyan" data-testid="cash-export"><DownloadSimple size={14} weight="bold" /> Export CSV</button>
      </div>
      {data === null ? <div className="card p-6 text-center text-sm text-white/45">Loading…</div>
        : items.length === 0 ? <div className="card p-8 text-center text-sm text-white/45">No transactions for {monthLabel(month)}.</div>
        : (
          <div className="space-y-2">
            {items.map((t) => {
              const isExp = t.type === "EXPENSE";
              return (
                <div key={t.id} className="card flex items-center gap-3 p-3.5" data-testid={`txn-${t.id}`}>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${isExp ? "bg-neon-pink/12 text-neon-pink" : "bg-neon-lime/12 text-neon-lime"}`}>
                    {isExp ? <ArrowUp size={18} weight="bold" /> : <ArrowDown size={18} weight="bold" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-display text-[14px] font-semibold text-white">{t.category}</p>
                      {t.flagged && <span className="inline-flex items-center gap-1 rounded-md border border-neon-amber/40 bg-neon-amber/10 px-1.5 py-0.5 text-[10px] font-bold text-neon-amber"><Flag size={10} weight="fill" /> Flagged</span>}
                    </div>
                    <p className="truncate text-[12px] text-white/55">{fmtDate(t.date)}{t.paidTo ? ` · ${t.paidTo}` : ""} · {t.paymentMode}{t.description ? ` · ${t.description}` : ""}</p>
                    {t.flagged && t.flagNote && <p className="text-[11px] text-neon-amber/80">“{t.flagNote}” — {t.flaggedByName}</p>}
                  </div>
                  {t.receiptUrl && <a href={t.receiptUrl} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white"><Receipt size={18} weight="duotone" /></a>}
                  <p className={`shrink-0 font-mono text-[15px] font-bold ${isExp ? "text-neon-pink" : "text-neon-lime"}`}>{isExp ? "-" : "+"}{inr(t.amount)}</p>
                  <div className="flex shrink-0 flex-col gap-1">
                    {canFlag && <button onClick={() => flag(t)} title="Flag for review" className={`rounded-lg p-1.5 ${t.flagged ? "text-neon-amber" : "text-white/40 hover:text-neon-amber"}`}><Flag size={14} weight={t.flagged ? "fill" : "bold"} /></button>}
                    <button onClick={() => setEdit(t)} title="Edit" className="rounded-lg p-1.5 text-white/40 hover:text-white"><PencilSimple size={14} weight="bold" /></button>
                    <button onClick={() => del(t)} title="Void" className="rounded-lg p-1.5 text-white/40 hover:text-neon-pink"><Trash size={14} weight="bold" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <AnimatePresence>
        {(addOpen || edit) && <EntrySheet txn={edit} onClose={() => { setAddOpen(false); setEdit(null); }} onSaved={() => { setAddOpen(false); setEdit(null); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function EntrySheet({ txn, onClose, onSaved }) {
  const [type, setType] = useState(txn?.type || "EXPENSE");
  const [amount, setAmount] = useState(txn ? String(txn.amount) : "");
  const [category, setCategory] = useState(txn?.category || CATEGORIES[0]);
  const [paidTo, setPaidTo] = useState(txn?.paidTo || "");
  const [mode, setMode] = useState(txn?.paymentMode || "CASH");
  const [date, setDate] = useState((txn?.date || istToday()).slice(0, 10));
  const [description, setDescription] = useState(txn?.description || "");
  const [receiptKey, setReceiptKey] = useState(txn?.receiptKey || null);
  const [receiptUrl, setReceiptUrl] = useState(txn?.receiptUrl || null);
  const [camOpen, setCamOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const amt = parseFloat(amount);
  const valid = amt > 0;

  async function onCapture(b64) {
    setCamOpen(false);
    try {
      const blob = await (await fetch(`data:image/jpeg;base64,${b64}`)).blob();
      const fd = new FormData(); fd.append("file", blob, "receipt.jpg");
      const { data } = await api.post("/api/cashbook/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptKey(data.receiptKey); setReceiptUrl(data.receiptUrl);
    } catch { setErr("Receipt upload failed (you can skip it)."); }
  }
  async function submit() {
    if (!valid) { setErr("Enter an amount."); return; }
    setBusy(true); setErr(null);
    const body = { type, amount: amt, category, paidTo: paidTo || null, paymentMode: mode, date, description: description || null, receiptKey };
    try {
      if (txn) await api.patch(`/api/cashbook/${txn.id}`, body);
      else await api.post("/api/cashbook", body);
      onSaved();
    } catch { setErr("Could not save. Try again."); setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="cash-entry-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">{txn ? "Edit entry" : "New entry"}</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-4">
          <div className="flex gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
            {[["EXPENSE", "Expense"], ["TOPUP", "Cash added"]].map(([k, l]) => (
              <button key={k} onClick={() => setType(k)} className={`flex-1 rounded-xl py-2 text-[13px] font-bold transition ${type === k ? (k === "EXPENSE" ? "bg-neon-pink text-black" : "bg-neon-lime text-black") : "text-white/65"}`}>{l}</button>
            ))}
          </div>
          <div>
            <label className="label">Amount (₹)</label>
            <input className="field text-2xl font-bold" data-testid="cash-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0" autoFocus />
          </div>
          {type === "EXPENSE" && (
            <div>
              <label className="label">Category</label>
              <select className="field" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cash-category">
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-ink-800">{c}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">{type === "EXPENSE" ? "Paid to (opt)" : "From (opt)"}</label>
              <input className="field" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} placeholder="e.g. Plumber" /></div>
            <div><label className="label">Mode</label>
              <select className="field" value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m} value={m} className="bg-ink-800">{m}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Date</label>
              <input type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="flex items-end">
              <button onClick={() => setCamOpen(true)} className="flex w-full items-center justify-center gap-1.5 rounded-2xl border border-white/15 bg-white/5 py-3 text-[12px] font-bold text-white/80">
                <Camera size={14} weight="bold" /> {receiptUrl ? "Retake receipt" : "Receipt"}
              </button>
            </div>
          </div>
          {receiptUrl && <img src={receiptUrl} alt="" className="h-20 rounded-xl object-cover" />}
          <div><label className="label">Note (opt)</label>
            <input className="field" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What was it for?" /></div>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} data-testid="cash-submit" className="btn-primary w-full disabled:opacity-50">{busy ? "Saving…" : (txn ? "Save changes" : "Record entry")}</button>
        </div>
      </motion.div>
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={onCapture} label="Receipt photo" />
    </motion.div>
  );
}
